import { CircularProgress, Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo } from 'react';

import { useGetCompanyInferenceQuery } from '~/services/rtkQuery/company';

import { AdaPrediction } from './components/AdaPrediction';
import { AskingValuation } from './components/AskingValuation';
import { CoInvestmentScore } from './components/CoInvestmentScore';
import { CoInvestorScore } from './components/CoInvestorScore';
import { ExitLikilihood } from './components/ExitLikilihood';
import { ExitZone } from './components/ExitZone';
import { InvestorsRating } from './components/InvestorsRating';
import { OverallInvestmentScore } from './components/OverallInvestmentScore';
import { SeriesCPrice } from './components/SeriesCPrice';
import { UnicornZone } from './components/UnicornZone';
import { UnicornZoneScatter } from './components/UnicornZoneScatter';
import styles from './styles.module.scss';

interface Props {
  companyId: number;
  generatingPdf: boolean;
}

const Component: React.FC<Props> = ({ companyId, generatingPdf }) => {
  const getCompanyInferenceQuery = useGetCompanyInferenceQuery({ id: companyId });

  if (getCompanyInferenceQuery.isLoading) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {getCompanyInferenceQuery.data && (
        <>
          <OverallInvestmentScore
            data={getCompanyInferenceQuery.data.result.data.investment_score}
            irrTh={getCompanyInferenceQuery.data.result.data.investment_score.irr_th}
            scatterChartData={
              getCompanyInferenceQuery.data.result.data.supporting_graphs.investment_score
            }
            score={getCompanyInferenceQuery.data.result.data.investment_score.score}
          />
          <div className={styles.container__charts}>
            <SeriesCPrice
              askingPrice={getCompanyInferenceQuery.data.result.data.asking_price}
              askingYPercent={getCompanyInferenceQuery.data.result.data.series_c_pricing.asking_y}
              barChartData={
                getCompanyInferenceQuery.data.result.data.supporting_graphs.series_c_pricing
              }
              data={getCompanyInferenceQuery.data.result.data.series_c_pricing}
              predValue={getCompanyInferenceQuery.data.result.data.series_c_pricing.fv}
              predYPercent={getCompanyInferenceQuery.data.result.data.series_c_pricing.pred_y}
            />
            <UnicornZone
              askingPrice={getCompanyInferenceQuery.data.result.data.asking_price}
              data={getCompanyInferenceQuery.data.result.data.unicorn_zone}
              pcaX={getCompanyInferenceQuery.data.result.data.unicorn_zone.pca_x}
              pcaY={getCompanyInferenceQuery.data.result.data.unicorn_zone.pca_y}
              scatterChartData={
                getCompanyInferenceQuery.data.result.data.supporting_graphs.unicorn_zone
              }
              uzXTh={getCompanyInferenceQuery.data.result.data.unicorn_zone.uz_x_th}
            />
            <ExitLikilihood
              data={getCompanyInferenceQuery.data.result.data.exit_score}
              kpcaFi={getCompanyInferenceQuery.data.result.data.exit_score.kpca_fi}
              kpcaRo={getCompanyInferenceQuery.data.result.data.exit_score.kpca_ro}
              scatterChartData={
                getCompanyInferenceQuery.data.result.data.supporting_graphs.exit_score
              }
            />
            <CoInvestorScore
              barChartData={
                getCompanyInferenceQuery.data.result.data.supporting_graphs.companies_scores
              }
              data={getCompanyInferenceQuery.data.result.data.investors_score}
              rating={getCompanyInferenceQuery.data.result.data.investors_score.rating}
            />
          </div>
          <div
            className={clsx(styles.container__reports, {
              [styles['container__reports--generating-pdf']]: generatingPdf,
            })}
          >
            <Typography color="text.primary" fontWeight="700" mb="21px" variant="subtitle1">
              Inference Reports
            </Typography>
            <div className={styles['container__reports-charts']}>
              <AdaPrediction
                data={getCompanyInferenceQuery.data.result.data.supporting_graphs.investment_score}
                irrTh={getCompanyInferenceQuery.data.result.data.investment_score.irr_th}
                score={getCompanyInferenceQuery.data.result.data.investment_score.score}
              />
              <UnicornZoneScatter
                data={getCompanyInferenceQuery.data.result.data.supporting_graphs.unicorn_zone}
                pcaX={getCompanyInferenceQuery.data.result.data.unicorn_zone.pca_x}
                pcaY={getCompanyInferenceQuery.data.result.data.unicorn_zone.pca_y}
                uzXTh={getCompanyInferenceQuery.data.result.data.unicorn_zone.uz_x_th}
              />
              <ExitZone
                data={getCompanyInferenceQuery.data.result.data.supporting_graphs.exit_score}
                kpcaFi={getCompanyInferenceQuery.data.result.data.exit_score.kpca_fi}
                kpcaRo={getCompanyInferenceQuery.data.result.data.exit_score.kpca_ro}
              />
              <AskingValuation
                askingPrice={getCompanyInferenceQuery.data.result.data.asking_price}
                askingYPercent={getCompanyInferenceQuery.data.result.data.series_c_pricing.asking_y}
                data={getCompanyInferenceQuery.data.result.data.supporting_graphs.series_c_pricing}
                predValue={getCompanyInferenceQuery.data.result.data.series_c_pricing.fv}
                predYPercent={getCompanyInferenceQuery.data.result.data.series_c_pricing.pred_y}
              />
              <CoInvestmentScore
                data={getCompanyInferenceQuery.data.result.data.supporting_graphs.companies_scores}
                rating={getCompanyInferenceQuery.data.result.data.investors_score.rating}
              />
            </div>
          </div>
          <InvestorsRating
            data={getCompanyInferenceQuery.data.result.data.supporting_graphs.investors_table}
          />
        </>
      )}
    </div>
  );
};

export const Inference = memo(Component);
